import { api } from './client';
import type { Product } from './catalog';

export interface Profile {
  email: string;
  firstName: string | null;
  lastName: string | null;
  country: string | null;
  addressLine1: string | null;
  city: string | null;
  postalCode: string | null;
  phone: string | null;
  birthDate: string | null;
  emailVerified?: boolean;
  ageVerified?: boolean;
  showAdultContent?: boolean;
  canEnableAdult?: boolean;
  kycStatus?: string;
  kycEnabled?: boolean;
  deletionScheduledAt?: string | null;
  [key: string]: unknown;
}

export async function requestAccountDeletion(): Promise<Profile> {
  const { data } = await api.post<Profile>('/users/me/deletion');
  return data;
}

export async function cancelAccountDeletion(): Promise<Profile> {
  const { data } = await api.delete<Profile>('/users/me/deletion');
  return data;
}

export interface ProfileUpdate {
  firstName?: string;
  lastName?: string;
  country?: string;
  addressLine1?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  birthDate?: string;
}

export async function updateProfile(patch: ProfileUpdate): Promise<Profile> {
  const { data } = await api.patch<Profile>('/users/me', patch);
  return data;
}

export async function updateContentPrefs(prefs: { ageVerified?: boolean; showAdultContent?: boolean }): Promise<Profile> {
  const { data } = await api.post<Profile>('/users/me/content-prefs', prefs);
  return data;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/users/me/password', { currentPassword, newPassword });
}

export interface Wallet {
  availableCents: number;
  heldCents: number;
  totalCents: number;
}

export async function fetchProfile(): Promise<Profile> {
  const { data } = await api.get<Profile>('/users/me');
  return data;
}

export async function fetchWallet(): Promise<Wallet> {
  const { data } = await api.get<Wallet>('/wallet');
  return data;
}

export interface WalletConfig {
  minDepositCents: number;
  maxDepositCents: number;
  currency: string;
}

export async function fetchWalletConfig(): Promise<WalletConfig> {
  const { data } = await api.get<WalletConfig>('/wallet/config');
  return data;
}

export interface TopupSession {
  paymentId: string;
  sessionId: string;
  redirectUrl: string;
  status: string;
  amountCents: number;
}

/** Start a hosted-checkout top-up; returns a provider redirect URL. */
export async function createTopupSession(amountCents: number): Promise<TopupSession> {
  const { data } = await api.post<TopupSession>('/wallet/topup-session', { amountCents });
  return data;
}

export interface TopupStatus {
  paymentId: string;
  amountCents: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | string;
}

/** Poll a top-up session's status (used by the provider return page). */
export async function getTopupSession(paymentId: string): Promise<TopupStatus> {
  const { data } = await api.get<TopupStatus>(`/wallet/topup-session/${paymentId}`);
  return data;
}

interface FavoriteRow { product: Product }
export async function fetchFavorites(limit = 5): Promise<Product[]> {
  const { data } = await api.get<{ items: FavoriteRow[] }>('/favorites', { params: { limit } });
  return (data.items ?? []).map((i) => i.product).filter(Boolean);
}

export async function addFavorite(productId: string): Promise<void> {
  await api.post('/favorites', { productId });
}

export async function removeFavorite(productId: string): Promise<void> {
  await api.delete(`/favorites/${productId}`);
}
